import { HttpClient } from '@angular/common/http';
import { DefaultDataServiceConfig } from '@ngrx/data';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export interface User {
  uuid?: string;
  created?: string;
  first_name: string;
  last_name: string;
  email: string;
  occupation?: string;
  status?: string;
  licenses?: string[];
  account?: string;
  is_admin?: boolean;
  location_uuids?: string[];
}

export interface License {
  uuid: string;
  license: {
    code: string;
    name: string;
    uuid: string;
    is_add_on: boolean;
    avatar: string | null;
    display_features: string[];
  };
  total_seats: number;
  used_seats: number;
}

export const UiFlags: any = {
  tableHeaders: 'pl-ui-table-headers',
  userEditHeader: 'pl-ui-user-edit-header',
};

@Injectable({ providedIn: 'root' })
export class AccountsService {
  constructor(
    private httpClient: HttpClient,
    private dataServiceConfig: DefaultDataServiceConfig
  ) {}

  fetchAccounts() {
    const accountsUrl = `${this.dataServiceConfig.root}/accounts/`;
    return this.httpClient.get(accountsUrl);
  }

  fetchAccountUsers(accountUuid: string) {
    const usersUrl = `${this.dataServiceConfig.root}/accounts/${accountUuid}/users/`;
    return this.httpClient.get(usersUrl);
  }

  fetchAccountKangaUser(accountUuid: string, userUuid: string) {
    const userUrl = `${this.dataServiceConfig.root}/accounts/${accountUuid}/users/${userUuid}`;
    return this.httpClient.get(userUrl);
  }

  fetchAccountLicenses(accountUuid: string) {
    const licensesUrl = `${this.dataServiceConfig.root}/accounts/${accountUuid}/licenses/`;
    return this.httpClient.get(licensesUrl);
  }

  getLicenseCounts(licenses: any[]) {
    const types: string[] = [];
    const results = licenses.reduce((accumulator: any, L: License) => {
      const code = L.license.code;
      types.push(code);
      accumulator[code] = {
        license: L.license,
        purchased: L.total_seats,
        used: L.used_seats,
        remaining: L.total_seats - L.used_seats,
        fraction: `${L.used_seats}/${L.total_seats}`,
        percentUsed: Math.floor((L.used_seats / L.total_seats) * 100),
        is_addOn: L.license.is_add_on,
        avatar: L.license.avatar ?? '?',
      };
      return accumulator;
    }, {});
    results.types = [...new Set(types)].sort((a: string, b: string) =>
      (results[a].license.name || a).localeCompare(results[b].license.name || b)
    );
    return results;
  }

  getLicenseCountsInfo(licenses: any) {
    const licenseCounts: any = {};

    const licenseInfo = this.getLicenseCounts(licenses);
    licenseInfo.types.forEach((type: string) => {
      licenseCounts[type] = {
        purchased: licenseInfo[type].purchased || 0,
        remaining: licenseInfo[type].remaining || 0,
        is_addOn: licenseInfo[type].is_addOn,
      };
    });
    licenseCounts.types = licenseInfo.types;
    licenseCounts.totalRemaining = 0;
    licenseCounts.totalRemarkableReading = 0;
    licenseInfo.types.forEach((type: string) => {
      if (type === 'remarkable_reading') {
        licenseCounts.totalRemarkableReading += licenseCounts[type].purchased;
      }
      if (!licenseCounts[type].is_addOn) {
        licenseCounts.totalRemaining += Math.max(
          licenseCounts[type].remaining,
          0
        );
      }
    });

    return licenseCounts;
  }

  save(user: User, accountUuid: string): Observable<User> {
    const method = user.uuid ? 'put' : 'post';
    const suffix = user.uuid ? `${user.uuid}/` : '';
    const url = `${this.dataServiceConfig.root}/accounts/${accountUuid}/users/${suffix}`;
    return (
      this.httpClient[method] as (url: string, body: any) => Observable<User>
    )(url, {
      ...user,
    });
  }
}
